import { useEffect, useState } from 'react';
import moment from 'moment';
import { Button, Col, Input, Label, Modal, Row, Spinner, Table } from 'reactstrap';
import DateTime from '../../../../components/Core/DateTime';

export type VerifiableDocument = {
  id: string;
  title: string;
  url?: string;
  expirationDate?: string | null;
  noExpiration?: boolean;
};

export type DocumentVerificationAnswer = {
  id: string;
  expirationDate?: string;
  noExpiration?: boolean;
};

type Answer = { expirationDate: string; noExpiration: boolean };

type VerifyEmploymentDocumentsModalProps = {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  documents: VerifiableDocument[];
  onConfirm: (answers: DocumentVerificationAnswer[]) => Promise<void>;
  toggleModal: () => void;
};

const PICKED_DATE_PATTERN = /^[A-Z][a-z]{2} [A-Z][a-z]{2} \d{2} \d{4}/;

const toStorageDate = (value: string): string => {
  if (!value || !PICKED_DATE_PATTERN.test(value)) return '';
  const parsed = moment(new Date(value));
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const toDisplayDate = (value: string): string => (value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : '');

const VerifyEmploymentDocumentsModal = ({
  isOpen,
  title,
  description,
  confirmLabel,
  documents,
  onConfirm,
  toggleModal,
}: VerifyEmploymentDocumentsModalProps) => {
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const initial: Record<string, Answer> = {};
    documents.forEach((document) => {
      initial[document.id] = {
        expirationDate: document.expirationDate ? String(document.expirationDate).slice(0, 10) : '',
        noExpiration: !!document.noExpiration,
      };
    });
    setAnswers(initial);
  }, [isOpen, documents]);

  const updateAnswer = (id: string, changes: Partial<Answer>) => {
    setAnswers((current) => ({ ...current, [id]: { ...current[id], ...changes } }));
  };

  const isComplete = documents.every((document) => {
    const answer = answers[document.id];
    return !!answer && (answer.noExpiration || !!answer.expirationDate);
  });

  const handleConfirm = async () => {
    if (!isComplete || saving) return;
    setSaving(true);
    try {
      await onConfirm(
        documents.map((document) => {
          const answer = answers[document.id];
          return answer.noExpiration
            ? { id: document.id, noExpiration: true }
            : { id: document.id, expirationDate: answer.expirationDate };
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal className="modal-dialog-centered" size="lg" isOpen={isOpen} toggle={toggleModal}>
      <div className="modal-header">
        <h5 className="modal-title">{title}</h5>
        <button aria-label="Close" className="close" type="button" onClick={toggleModal}>
          <span aria-hidden={true}>×</span>
        </button>
      </div>
      <div className="modal-body">
        <p className="text-sm mb-3">{description}</p>
        <Table className="align-items-center mb-0" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th>Document</th>
              <th style={{ width: 280 }}>Expiration date</th>
              <th style={{ width: 170 }}>No expiration</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => {
              const answer = answers[document.id] || { expirationDate: '', noExpiration: false };
              return (
                <tr key={document.id}>
                  <td>
                    {document.url ? (
                      <a href={document.url} target="_blank" rel="noopener noreferrer">
                        {document.title}
                      </a>
                    ) : (
                      document.title
                    )}
                  </td>
                  <td>
                    <DateTime
                      value={answer.noExpiration ? '' : toDisplayDate(answer.expirationDate)}
                      inputProps={{ placeholder: 'MM/DD/YYYY', disabled: answer.noExpiration }}
                      onChange={(newValue) => updateAnswer(document.id, { expirationDate: toStorageDate(newValue) })}
                    />
                  </td>
                  <td>
                    <Row className="align-items-center m-0">
                      <Col className="p-0">
                        <Label className="custom-control custom-checkbox mb-0" style={{ cursor: 'pointer' }}>
                          <Input
                            type="checkbox"
                            checked={answer.noExpiration}
                            onChange={(e) =>
                              updateAnswer(document.id, {
                                noExpiration: e.target.checked,
                                expirationDate: e.target.checked ? '' : answer.expirationDate,
                              })
                            }
                          />{' '}
                          Does not expire
                        </Label>
                      </Col>
                    </Row>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {!isComplete && (
          <p className="text-sm text-warning mb-0">
            Enter the expiration date of every document, or mark it as not expiring.
          </p>
        )}
      </div>
      <div className="modal-footer">
        <Button color="secondary" outline onClick={toggleModal} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={!isComplete || saving}>
          {saving ? <Spinner size="sm" /> : confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default VerifyEmploymentDocumentsModal;
